import { useEffect, useState } from "react";
import { useStore } from "../lib/store";
import * as tts from "../lib/tts";
import type { Lang, LangProfile } from "../lib/types";
import { LANG_NAMES, LANGS } from "../lib/types";

const NOTES: Record<Lang, string> = {
  fr: "French is set up for reactivation: high volume, no within-session repeats, production tested first, and longer intervals. Most cards will be answered correctly on sight, and the scheduler should push those far out rather than drilling them.",
  es: "Spanish is set up for acquisition: low volume, audio on every card, recognition before production, and learning steps left on so a new word is seen twice in a session.",
};

export default function Settings() {
  const { profiles, saveProfile, lastSync, pending, syncNow, syncing, syncError } = useStore();
  const [saved, setSaved] = useState<Lang | null>(null);

  return (
    <>
      <h1>Settings</h1>
      <p className="lede">
        The two languages are configured separately because they are different problems.
      </p>

      {LANGS.map((lang) => (
        <ProfileEditor
          key={lang}
          lang={lang}
          profile={profiles[lang]}
          saved={saved === lang}
          onSave={async (next) => {
            await saveProfile(lang, next);
            setSaved(lang);
            setTimeout(() => setSaved(null), 2000);
          }}
        />
      ))}

      <h2>Sync</h2>
      <div className="card stack">
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="k">Last synced</span>
          <span>{lastSync ? new Date(lastSync).toLocaleString() : "never"}</span>
        </div>
        <div className="row" style={{ justifyContent: "space-between" }}>
          <span className="k">Waiting to upload</span>
          <span>{pending}</span>
        </div>
        {syncError && <div className="notice error">{syncError}</div>}
        <button className="btn" disabled={syncing} onClick={() => void syncNow()}>
          {syncing ? <><span className="spinner" /> Syncing…</> : "Sync now"}
        </button>
      </div>
    </>
  );
}

function ProfileEditor({
  lang, profile, saved, onSave,
}: {
  lang: Lang;
  profile: LangProfile;
  saved: boolean;
  onSave: (profile: LangProfile) => Promise<void>;
}) {
  const [draft, setDraft] = useState<LangProfile>(profile);
  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);

  const set = <K extends keyof LangProfile>(key: K, value: LangProfile[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <>
      <h2>{LANG_NAMES[lang]}</h2>
      <p className="lede" style={{ marginTop: -4 }}>{NOTES[lang]}</p>

      <div className="card stack">
        <label className="field">
          New cards per day: {draft.newPerDay}
          <input
            type="range" min={0} max={100} step={5}
            value={draft.newPerDay}
            onChange={(e) => set("newPerDay", Number(e.target.value))}
          />
        </label>

        <label className="field">
          Maximum cards per day: {draft.maxPerDay}
          <input
            type="range" min={20} max={400} step={10}
            value={draft.maxPerDay}
            onChange={(e) => set("maxPerDay", Number(e.target.value))}
          />
        </label>

        <label className="field">
          Target retention: {Math.round(draft.requestRetention * 100)}%
          <input
            type="range" min={0.8} max={0.97} step={0.01}
            value={draft.requestRetention}
            onChange={(e) => set("requestRetention", Number(e.target.value))}
          />
        </label>
        <p className="lede" style={{ marginTop: -6, fontSize: 12 }}>
          Lower means longer intervals and more forgotten cards; higher means more reviews
          for the same collection. 90% is the usual sweet spot.
        </p>

        <label className="field">
          Suspend after this many lapses: {draft.leechThreshold}
          <input
            type="range" min={3} max={12} step={1}
            value={draft.leechThreshold}
            onChange={(e) => set("leechThreshold", Number(e.target.value))}
          />
        </label>

        <Toggle
          label="Test production as well as recognition"
          hint="Two separately scheduled cards per note."
          checked={draft.production}
          onChange={(v) => set("production", v)}
        />
        <Toggle
          label="Introduce the production card first"
          hint="Right when recognition is already intact and recall is the gap."
          checked={draft.productionFirst}
          onChange={(v) => set("productionFirst", v)}
        />
        <Toggle
          label="Use learning steps"
          hint="Off means a card answered correctly is scheduled in days, never re-shown minutes later."
          checked={draft.shortTermSteps}
          onChange={(v) => set("shortTermSteps", v)}
        />
        <VoicePicker
          lang={lang}
          enabled={draft.audioEnabled}
          autoplay={draft.autoplayAudio}
          voiceURI={draft.voiceURI}
          onChange={(patch) => setDraft((current) => ({ ...current, ...patch }))}
        />

        <button className="btn primary" disabled={!dirty} onClick={() => void onSave(draft)}>
          {saved ? "Saved" : "Save"}
        </button>
      </div>
    </>
  );
}

function Toggle({
  label, hint, checked, onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="switch">
      <span>
        {label}
        {hint && <div className="lede" style={{ margin: 0, fontSize: 12 }}>{hint}</div>}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

/**
 * Audio settings, including which voice to use.
 *
 * A picker rather than a fixed choice because voice quality varies enormously
 * by device: the same code sounds fine on a phone and robotic on a Windows
 * desktop. The Test button matters more than the list — the only way to judge
 * a voice is to hear it say something in the language.
 */
function VoicePicker({
  lang, enabled, autoplay, voiceURI, onChange,
}: {
  lang: Lang;
  enabled: boolean;
  autoplay: boolean;
  voiceURI: string | null;
  onChange: (patch: Partial<LangProfile>) => void;
}) {
  const [voices, setVoices] = useState(() => tts.listVoices(lang));

  // Browsers populate the voice list asynchronously, and iOS not until after a
  // user gesture, so an empty list on first render is normal.
  useEffect(() => {
    setVoices(tts.listVoices(lang));
    return tts.onVoicesChanged(() => setVoices(tts.listVoices(lang)));
  }, [lang]);

  if (!tts.isSupported()) {
    return <div className="notice">This browser has no speech synthesis, so card audio is unavailable.</div>;
  }

  const selected = tts.resolveVoice(lang, voiceURI);

  return (
    <>
      <Toggle
        label="Card audio"
        hint="Off removes the speaker button as well."
        checked={enabled}
        onChange={(v) => onChange({ audioEnabled: v })}
      />

      {enabled && (
        <>
          <Toggle
            label="Speak cards automatically"
            checked={autoplay}
            onChange={(v) => onChange({ autoplayAudio: v })}
          />

          <label className="field">
            Voice
            <select
              value={voiceURI ?? ""}
              onChange={(e) => onChange({ voiceURI: e.target.value || null })}
              disabled={voices.length === 0}
            >
              <option value="">
                {voices.length === 0
                  ? "No voices installed for this language"
                  : `Best available (${selected?.name ?? "none"})`}
              </option>
              {voices.map((voice) => (
                <option key={voice.voiceURI} value={voice.voiceURI}>
                  {voice.name}
                  {voice.localService ? "" : " · online"}
                </option>
              ))}
            </select>
          </label>

          <button
            className="btn"
            disabled={voices.length === 0}
            onClick={() => tts.speak(tts.SAMPLE[lang], lang, { voiceURI })}
          >
            Test this voice
          </button>

          {voices.length === 0 ? (
            <p className="lede" style={{ margin: 0, fontSize: 12 }}>
              Nothing installed for {LANG_NAMES[lang]}. On Windows: Settings → Time &amp; Language →
              Language &amp; region → add the language → Language options → install Speech. Phones
              generally ship usable voices already.
            </p>
          ) : (
            <p className="lede" style={{ margin: 0, fontSize: 12 }}>
              Voices marked “online” are usually the better ones on Windows — the offline Microsoft
              voices are the old robotic set. Phones sound considerably better than a desktop here,
              so judge this on the device you will actually review on.
            </p>
          )}
        </>
      )}
    </>
  );
}
